/**
 * Every URL this console serves, and every URL it used to serve.
 *
 * One table, so a route and the redirect pointing at it cannot drift apart —
 * a test asserts every redirect target and every nav tab resolves to something
 * in ROUTES.
 *
 * ORDER MATTERS in the tenant block. `/tenants/access` and `/tenants/evidence`
 * must be declared BEFORE `/tenants/:ref`, or the router matches "access" as a
 * business reference and renders the merchant page for a merchant called
 * "access". React Router v6 ranks static segments above dynamic ones so this
 * is belt-and-braces rather than load-bearing — but the failure is silent and
 * looks like a 404 from the API, so the order is pinned by a test too.
 */
export interface RouteDef {
  path: string;
  /** Which page component renders it — resolved in App.tsx. */
  page: string;
}

export const ROUTES: RouteDef[] = [
  { path: "/", page: "FleetPosture" },

  // --- Tenants. Static paths first; see the note above. ---
  { path: "/tenants", page: "Tenants" },
  { path: "/tenants/access", page: "Access" },
  { path: "/tenants/evidence", page: "FleetEvidence" },
  { path: "/tenants/:ref", page: "Tenant" },
  { path: "/tenants/:ref/usage", page: "TenantUsage" },
  { path: "/tenants/:ref/history", page: "TenantHistory" },
  { path: "/tenants/:ref/users", page: "TenantUsers" },
  { path: "/tenants/:ref/users/:email", page: "TenantUser" },

  // --- Policy ---
  { path: "/policy/tiers", page: "Tiers" },
  { path: "/policy/caps", page: "Caps" },
  { path: "/policy/caps/:tier", page: "CapsTier" },
  { path: "/policy/beta", page: "BetaGrants" },
  { path: "/policy/flags", page: "Flags" },
  { path: "/policy/hitl", page: "HitlGates" },
  { path: "/policy/welcome", page: "Welcome" },

  // --- Governance ---
  { path: "/governance/permissions", page: "Permissions" },
  { path: "/governance/audit", page: "Audit" },
];

/**
 * Old URL → new URL. Nothing anyone bookmarked or pasted into a ticket breaks.
 *
 * `:param` in a target is filled from the matched route's params, so a
 * reference needing URL-encoding survives the hop. The previous
 * implementation read `window.location.pathname` and split on "/", which
 * mangled exactly those.
 *
 * Two generations are listed: the pre-`/mcp` routes (already redirects before
 * this change) and the `/mcp/*` ones this restructure replaces. The oldest
 * therefore takes two hops, which is fine — a redirect chain costs nothing and
 * collapsing it would mean remembering two mappings instead of one.
 */
export const REDIRECTS: { from: string; to: string }[] = [
  // Pre-/mcp, kept from the previous restructure.
  { from: "/merchants", to: "/tenants" },
  { from: "/merchants/:ref", to: "/tenants/:ref" },

  // The /mcp/* generation.
  { from: "/mcp", to: "/" },
  { from: "/mcp/merchants", to: "/tenants" },
  { from: "/mcp/merchants/:ref", to: "/tenants/:ref" },
  { from: "/mcp/merchants/:ref/usage", to: "/tenants/:ref/usage" },
  { from: "/mcp/merchants/:ref/history", to: "/tenants/:ref/history" },
  { from: "/mcp/merchants/:ref/users", to: "/tenants/:ref/users" },
  { from: "/mcp/merchants/:ref/users/:email", to: "/tenants/:ref/users/:email" },
  { from: "/mcp/blocks", to: "/tenants/access" },
  { from: "/mcp/quotas", to: "/policy/caps" },
  { from: "/mcp/quotas/:tier", to: "/policy/caps/:tier" },
  { from: "/mcp/catalog", to: "/policy/caps" },
  { from: "/mcp/settings", to: "/policy/flags" },
  { from: "/status", to: "/governance/permissions" },

  // Folded into the design's own surfaces, so the console's nav is exactly the
  // twelve tabs the design draws. The content did not go away — it moved:
  //   the tool catalog  -> a panel under Tool caps
  //   MCP settings      -> Feature flags, beside the Labeeb flags it belongs with
  //   product health    -> Permission map
  //   your access       -> Permission map, which already showed it
  { from: "/policy/catalog", to: "/policy/caps" },
  { from: "/governance/settings", to: "/policy/flags" },
  { from: "/governance/health", to: "/governance/permissions" },
  { from: "/me", to: "/governance/permissions" },
];
